import { Badge } from "@/ds/shadcn/components/ui/badge";
import { Button } from "@/ds/shadcn/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/ds/shadcn/components/ui/input-group";
import { ModalFilter } from "@/features/common/components/ModalFilter";
import { useDebouncedState } from "@/lib/common/useDebouncedState";
import { getSearchQueryFromSearchString, getSearchStringFromSearchQuery, type Filter, type SearchQuery } from "@/lib/domain/FilterConverters";
import type { FilterField } from "@/lib/domain/FilterField";
import { filterOperatorToWords } from "@/lib/domain/FilterOperator";
import { FunnelPlusIcon, SearchIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";

export interface SearchBarProps {
	value: string;
	onChange: (value: string) => void;
}

export const SearchBar: FC<SearchBarProps> = props => {
	const {
		value,
		onChange
	} = props;

	const [modalOpen, setModalOpen] = useState(false);
	const [debouncedSearchString, searchString, setSearchString] = useDebouncedState(value, 500);
	const searchQuery = useMemo(() => getSearchQueryFromSearchString(debouncedSearchString), [debouncedSearchString]);

	useEffect(() => {
		if (!searchQuery) {
			return;
		}

		onChange(getSearchStringFromSearchQuery(searchQuery));
	}, [searchQuery]);

	const onModalConfirm = useCallback((newFilter: Filter) => {
		const currentSearchQuery = searchQuery || {
			filters: [],
			segments: []
		};

		const updatedFilters = [...currentSearchQuery.filters, newFilter];
		const updatedSearchQuery: SearchQuery = {
			...currentSearchQuery,
			filters: updatedFilters
		};

		setSearchString(getSearchStringFromSearchQuery(updatedSearchQuery));
	}, [searchQuery]);

	const onFilterRemove = useCallback((filter: FilterField) => {
		if (!searchQuery) {
			return;
		}

		const updatedFilters = searchQuery.filters.filter(x => x.field !== filter);
		const updatedSearchQuery: SearchQuery = {
			...searchQuery,
			filters: updatedFilters
		};

		setSearchString(getSearchStringFromSearchQuery(updatedSearchQuery));
	}, [searchQuery]);

	const forbiddenFields = useMemo(() => {
		if (!searchQuery) {
			return [];
		}

		return searchQuery.filters.map(x => x.field);
	}, [searchQuery]);

	return (
		<div className="flex flex-col gap-3 border-b pb-3">
			<div className="px-3 pt-3 flex items-center gap-3">
				<InputGroup>
					<InputGroupAddon>
						<SearchIcon />
					</InputGroupAddon>
					<InputGroupInput placeholder="Search" value={searchString} onChange={e => setSearchString(e.currentTarget.value)} />
				</InputGroup>

				<Button variant="outline" onClick={() => setModalOpen(true)}>
					<FunnelPlusIcon />
				</Button>

				<ModalFilter open={modalOpen} onOpenChange={setModalOpen} onConfirm={onModalConfirm} forbiddenFields={forbiddenFields} />
			</div>

			{
				searchQuery && searchQuery.filters.length > 0 &&
				<div className="flex w-full flex-wrap gap-3 items-center px-3 overflow-hidden">
					{
						searchQuery.filters.map(x => (
							<Badge className="cursor-pointer" key={x.field} variant="secondary" onClick={() => onFilterRemove(x.field)}>
								{x.field} {filterOperatorToWords(x.operator, x.operands.length)} {x.operands.join(",")}
								<XIcon />
							</Badge>
						))
					}
				</div>
			}
		</div>
	);
};
